// Session refresh helper. Hits POST /v5/auth/refresh to mint a new
// access cookie before the old one expires (timer) or right after a 401
// (lazy retry). Both call sites share one in-flight promise so we never
// run two concurrent refreshes — important on /401-retry where multiple
// requests can race the same expired cookie.

import { apiBaseUrl } from '../lib/env';
import { csrfToken } from './csrf';

let inflight: Promise<boolean> | null = null;

/**
 * Call POST /v5/auth/refresh. Returns true if the server minted a new
 * access cookie, false if the refresh token is gone / invalid (the
 * caller should treat this as logged-out).
 *
 * Idempotent across concurrent calls: returns the same promise to all
 * callers within a single refresh window.
 */
export function refreshSession(): Promise<boolean> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const csrf = csrfToken();
      const res = await fetch(`${apiBaseUrl}/v5/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      });
      // 401 = refresh token also gone / revoked — game over.
      return res.ok;
    } catch {
      return false;
    } finally {
      // Hold the inflight promise for ~1s so a burst of 401 retries all
      // see the same result and don't immediately stampede again.
      setTimeout(() => { inflight = null; }, 1000);
    }
  })();

  return inflight;
}
